import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import {
  ApiResponse,
  CasePlanStatusModel,
} from '../../features/insights/insights.interface';

@Injectable({
  providedIn: 'root',
})
export class CasePlanStatusService {
  private readonly API_URL = `${environment.apiUrl}`;
  private readonly statusSubject =
    new BehaviorSubject<CasePlanStatusModel | null>(null);
  status$ = this.statusSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlingService
  ) {}

  fetchStatus(caseId: number): Observable<ApiResponse<CasePlanStatusModel>> {
    // Clear immediately so switching cases never briefly renders the
    // previous case's gating status while the fresh fetch is in flight.
    this.statusSubject.next(null);
    return this.http
      .get<
        ApiResponse<CasePlanStatusModel>
      >(`${this.API_URL}/case/get-pending-items?caseId=${caseId}`)
      .pipe(
        tap(response => this.statusSubject.next(response?.result ?? null)),
        catchError(error => {
          if (error?.status === 404) {
            const fallback: ApiResponse<CasePlanStatusModel> = {
              result: {
                caseId,
                questionnaire: {
                  hasPendingHardStop: false,
                  hasPendingRequired: false,
                  pendingRequiredItems: [],
                  pendingHardStopItems: [],
                },
                behaviorList: [],
              },
              message: '',
              statusCode: 200,
              errors: [],
            };
            this.statusSubject.next(fallback.result);
            return new Observable<ApiResponse<CasePlanStatusModel>>(
              subscriber => {
                subscriber.next(fallback);
                subscriber.complete();
              }
            );
          }
          this.statusSubject.next(null);
          return this.errorHandler.handleError(error);
        })
      );
  }

  isCaseGatingClear(status: CasePlanStatusModel | null): boolean {
    const questionnaire = status?.questionnaire;
    return (
      !questionnaire?.hasPendingHardStop && !questionnaire?.hasPendingRequired
    );
  }

  isBehaviorGatingClear(
    status: CasePlanStatusModel | null,
    behaviorId: number
  ): boolean {
    const behaviorStatus = status?.behaviorList?.find(
      b => b.behaviorId === behaviorId
    );
    if (!behaviorStatus) {
      return true;
    }
    return (
      !behaviorStatus.hasPendingHardStop && !behaviorStatus.hasPendingRequired
    );
  }
}
